import { createContext, useContext, useEffect, useRef, useState } from "react";
import { getAuthToken, refreshAccessToken, setActivityCallback } from "../api/agentApi";
import { PERMISSIONS } from "../permissions";

const UserContext = createContext();

function normalizePermissions(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function normalizeRoles(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

export function isAdminUser(user) {
  const legacyRole = String(user?.role || "").toLowerCase();
  const roleSlugs = normalizeRoles(user?.roles).map((role) => String(role).toLowerCase());
  return legacyRole === "admin" || legacyRole === "operator-admin" || roleSlugs.includes("administrator") || roleSlugs.includes("admin");
}

function legacyPermissionsForRole(role) {
  if (role === "admin" || role === "operator-admin") {
    return Object.values(PERMISSIONS);
  }
  if (role === "developer") {
    return [
      PERMISSIONS.AGENTS_READ,
      PERMISSIONS.AGENTS_CREATE,
      PERMISSIONS.AGENTS_UPDATE,
      PERMISSIONS.AGENTS_RUN,
      PERMISSIONS.WORKFLOWS_READ,
      PERMISSIONS.WORKFLOWS_CREATE,
      PERMISSIONS.WORKFLOWS_UPDATE,
      PERMISSIONS.WORKFLOWS_RUN,
      PERMISSIONS.TOOLS_READ,
      PERMISSIONS.TOOLS_CREATE,
      PERMISSIONS.KNOWLEDGE_READ,
      PERMISSIONS.KNOWLEDGE_CREATE,
      PERMISSIONS.KNOWLEDGE_UPDATE,
      PERMISSIONS.RUNS_READ,
      PERMISSIONS.RUNS_START,
    ];
  }
  return [];
}

function withPermissionFallback(user) {
  if (!user) return null;
  if (normalizePermissions(user.permissions).length > 0) return user;
  return {
    ...user,
    permissions: legacyPermissionsForRole(user.role),
  };
}

function decodeJwt(token) {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return withPermissionFallback({
      id: decoded["id"],
      username: decoded["sub"],
      role: decoded["role"],
      roles: normalizeRoles(decoded["roles"]),
      permissions: normalizePermissions(decoded["permissions"]),
      exp: decoded["exp"],
      token,
    });
  } catch (e) {
    return null;
  }
}

function isExpired(decodedUser) {
  if (!decodedUser?.exp) return true;
  return decodedUser.exp <= Date.now() / 1000;
}

function clearStoredUserSession() {
  localStorage.removeItem("agentui_user");
  localStorage.removeItem("authToken");
}

function readInitialUser() {
  const token = localStorage.getItem("authToken");
  const decoded = token ? decodeJwt(token) : null;
  if (!decoded || isExpired(decoded)) {
    clearStoredUserSession();
    return null;
  }
  localStorage.setItem("agentui_user", JSON.stringify(decoded));
  return decoded;
}

export function UserProvider({ children }) {
  const [user, setUser] = useState(readInitialUser);

  const [passwordSetByOperator, setPasswordSetByOperator] = useState(() => {
    const saved = localStorage.getItem("agentui_passwordSetByOperator");
    return saved ? JSON.parse(saved) : false;
  });

  const [requiresPasswordChange, setRequiresPasswordChange] = useState(() => {
    const saved = localStorage.getItem("agentui_requiresPasswordChange");
    return saved ? JSON.parse(saved) : false;
  });

  const lastActivityRef = useRef(Date.now());
  const refreshInFlight = useRef(false);
  const mountedRef = useRef(true);
  const userSessionActiveRef = useRef(!!user);

  // Update userSessionActiveRef when user changes
  useEffect(() => {
    userSessionActiveRef.current = !!user;
  }, [user]);

  useEffect(() => {
    mountedRef.current = true;

    // Treat any network/UI activity as activity
    const markActive = () => {
      lastActivityRef.current = Date.now();
    };
    setActivityCallback(markActive);

    const activityEvents = ["mousemove", "keydown", "click", "scroll", "touchstart"]; // coarse but cheap
    activityEvents.forEach(evt => window.addEventListener(evt, markActive));

    const REFRESH_CHECK_MS = 60 * 1000; // check every minute
    const REFRESH_THRESHOLD_SECONDS = 5 * 60; // refresh when <=5 minutes left
    const IDLE_GRACE_MS = 10 * 60 * 1000; // if idle >10 minutes, skip refresh to avoid wasted cycles

    const intervalId = setInterval(async () => {
      // Check if user session is still active using ref to avoid stale closure
      if (!userSessionActiveRef.current) return;
      
      const token = getAuthToken();
      if (!token) return;

      const decoded = decodeJwt(token);
      if (!decoded || !decoded.exp) {
        return;
      }

      const nowSec = Date.now() / 1000;
      const timeLeft = decoded.exp - nowSec;

      const idleMs = Date.now() - lastActivityRef.current;
      const isIdle = idleMs > IDLE_GRACE_MS;

      if (timeLeft <= REFRESH_THRESHOLD_SECONDS && !isIdle && !refreshInFlight.current) {
        refreshInFlight.current = true;
        try {
          const newToken = await refreshAccessToken();
          // Check for both null (401) and undefined (missing access_token)
          if (newToken != null && mountedRef.current) {
            const userObj = decodeJwt(newToken);
            setUser(userObj);
            localStorage.setItem("agentui_user", JSON.stringify(userObj));
            localStorage.setItem("authToken", newToken);
          }
        } catch (e) {
          // Only update auth state if the component is still mounted
          if (!mountedRef.current) {
            return;
          }

          // Distinguish authentication errors from other failures (e.g., network issues)
          const status = (e && e.response && e.response.status) || e?.status;
          if (status === 401 || status === 403) {
            // If refresh fails due to auth, log out to avoid using a stale token
            setUser(null);
            localStorage.removeItem("agentui_user");
            localStorage.removeItem("authToken");
          } else {
            // For non-auth errors (network/transient), keep current auth state
            // Optionally log for diagnostics
            // console.error("Token refresh failed with non-auth error:", e);
          }
        } finally {
          refreshInFlight.current = false;
        }
      }
    }, REFRESH_CHECK_MS);

    return () => {
      mountedRef.current = false;
      setActivityCallback(null);
      activityEvents.forEach(evt => window.removeEventListener(evt, markActive));
      clearInterval(intervalId);
    };
  }, []);

  const login = (token, loginResponse = {}) => {
    const decodedUser = decodeJwt(token);
    const userObj = decodedUser
      ? {
          ...decodedUser,
          roles: normalizeRoles(loginResponse.roles || decodedUser.roles),
          permissions: normalizePermissions(loginResponse.permissions || decodedUser.permissions),
        }
      : null;
    const resolvedUserObj = withPermissionFallback(userObj);
    setUser(resolvedUserObj);
    localStorage.setItem("agentui_user", JSON.stringify(resolvedUserObj));
    localStorage.setItem("authToken", token);

    // Track password_set_by_operator flag from login response
    const passwordSet = loginResponse.password_set_by_operator || false;
    setPasswordSetByOperator(passwordSet);
    localStorage.setItem("agentui_passwordSetByOperator", JSON.stringify(passwordSet));

    // Determine if password change is required:
    // User-like roles need forced password change if password_set_by_operator is true.
    const role = loginResponse.role || userObj?.role;
    const requires = passwordSet && (role === 'user' || role === 'developer');
    setRequiresPasswordChange(requires);
    localStorage.setItem("agentui_requiresPasswordChange", JSON.stringify(requires));

    lastActivityRef.current = Date.now();
  };

  const clearPasswordChangeFlag = () => {
    setRequiresPasswordChange(false);
    setPasswordSetByOperator(false);
    localStorage.removeItem("agentui_requiresPasswordChange");
    localStorage.removeItem("agentui_passwordSetByOperator");
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("agentui_user");
    localStorage.removeItem("authToken");
    clearPasswordChangeFlag();
  };

  const permissions = normalizePermissions(user?.permissions);
  const permissionSet = new Set(permissions);
  const can = (permission) => permissionSet.has(permission);
  const canAny = (required = []) => required.some((permission) => permissionSet.has(permission));
  const canAll = (required = []) => required.every((permission) => permissionSet.has(permission));

  return (
    <UserContext.Provider value={{
      user,
      permissions,
      can,
      canAny,
      canAll,
      login,
      logout,
      passwordSetByOperator,
      requiresPasswordChange,
      clearPasswordChangeFlag
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
